import { useEffect } from 'react';
import { Check, AlertTriangle, X, Info } from 'lucide-react';
import { palette } from '../../styles/tokens';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  isVisible: boolean;
  onDismiss: () => void;
}

const CONFIG: Record<string, { Icon: typeof Check; color: string; bg: string }> = {
  success: { Icon: Check, color: palette.green, bg: palette.greenWash },
  error: { Icon: X, color: palette.red, bg: palette.redWash },
  warning: { Icon: AlertTriangle, color: palette.amber, bg: palette.amberWash },
  info: { Icon: Info, color: palette.blue, bg: palette.blueWash },
};

export function Toast({ message, type, isVisible, onDismiss }: ToastProps) {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onDismiss, 4000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onDismiss]);

  const c = CONFIG[type] ?? CONFIG.success;

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 100,
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px', borderRadius: 12,
      background: palette.white,
      borderLeft: `3px solid ${c.color}`,
      boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
      fontSize: 13, fontWeight: 500, color: palette.g800,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      opacity: isVisible ? 1 : 0,
      pointerEvents: isVisible ? 'auto' : 'none',
      transition: 'all 250ms ease',
      maxWidth: 380,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <c.Icon size={14} color={c.color} strokeWidth={2.5} />
      </div>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onDismiss} style={{
        width: 20, height: 20, borderRadius: 10, border: 'none',
        background: palette.g100, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <X size={10} color={palette.g500} />
      </button>
    </div>
  );
}
